// Typed AST for Pine v5 strategy programs.
//
// One canonical representation drives both deterministic preview generation
// and (later) the visual condition composer. Templates produce ASTs; the
// renderer in render.ts turns an AST into compilable Pine source.

export type PineBinaryOp =
  | "and"
  | "or"
  | "=="
  | "!="
  | ">"
  | "<"
  | ">="
  | "<="
  | "+"
  | "-"
  | "*"
  | "/";

export type PineUnaryOp = "not" | "-";

export type PineExpr =
  | { kind: "lit-string"; value: string }
  | { kind: "lit-number"; value: number }
  | { kind: "lit-bool"; value: boolean }
  | { kind: "lit-na" }
  | { kind: "ident"; name: string }
  | { kind: "member"; object: PineExpr; property: string }
  | {
      kind: "call";
      callee: string;
      args: PineExpr[];
      namedArgs?: Array<[string, PineExpr]>;
    }
  | { kind: "binary"; op: PineBinaryOp; left: PineExpr; right: PineExpr }
  | { kind: "unary"; op: PineUnaryOp; operand: PineExpr }
  | { kind: "ternary"; cond: PineExpr; then: PineExpr; otherwise: PineExpr }
  | { kind: "paren"; inner: PineExpr };

export type PineVarDeclaration = "var float" | "var int" | "var bool";

export type PineStmt =
  | { kind: "comment"; text: string }
  | { kind: "blank" }
  | { kind: "version"; version: 5 }
  | {
      kind: "strategy-decl";
      name: string;
      namedArgs: Array<[string, PineExpr]>;
    }
  | {
      kind: "input-int";
      name: string;
      defaultValue: number;
      title: string;
      minval?: number;
      maxval?: number;
      step?: number;
    }
  | {
      kind: "input-float";
      name: string;
      defaultValue: number;
      title: string;
      minval?: number;
      maxval?: number;
      step?: number;
    }
  | {
      kind: "input-bool";
      name: string;
      defaultValue: boolean;
      title: string;
    }
  | {
      kind: "assign";
      target: string | string[];
      value: PineExpr;
      declaration?: PineVarDeclaration;
    }
  | { kind: "reassign"; target: string; value: PineExpr }
  | { kind: "if"; condition: PineExpr; body: PineStmt[] }
  | {
      kind: "call-stmt";
      callee: string;
      args: PineExpr[];
      namedArgs?: Array<[string, PineExpr]>;
    };

export type PineProgram = { statements: PineStmt[] };

// Convenience constructors. Keeping these here means template builders read
// like data, not like string concatenation.

export const expr = {
  str(value: string): PineExpr {
    return { kind: "lit-string", value };
  },
  num(value: number): PineExpr {
    return { kind: "lit-number", value };
  },
  bool(value: boolean): PineExpr {
    return { kind: "lit-bool", value };
  },
  na(): PineExpr {
    return { kind: "lit-na" };
  },
  id(name: string): PineExpr {
    return { kind: "ident", name };
  },
  member(object: PineExpr | string, property: string): PineExpr {
    const obj = typeof object === "string" ? expr.id(object) : object;
    return { kind: "member", object: obj, property };
  },
  call(
    callee: string,
    args: PineExpr[] = [],
    namedArgs?: Array<[string, PineExpr]>,
  ): PineExpr {
    return { kind: "call", callee, args, namedArgs };
  },
  bin(op: PineBinaryOp, left: PineExpr, right: PineExpr): PineExpr {
    return { kind: "binary", op, left, right };
  },
  unary(op: PineUnaryOp, operand: PineExpr): PineExpr {
    return { kind: "unary", op, operand };
  },
  tern(cond: PineExpr, then: PineExpr, otherwise: PineExpr): PineExpr {
    return { kind: "ternary", cond, then, otherwise };
  },
  paren(inner: PineExpr): PineExpr {
    return { kind: "paren", inner };
  },
};

export const stmt = {
  comment(text: string): PineStmt {
    return { kind: "comment", text };
  },
  blank(): PineStmt {
    return { kind: "blank" };
  },
  version(): PineStmt {
    return { kind: "version", version: 5 };
  },
  strategyDecl(name: string, namedArgs: Array<[string, PineExpr]>): PineStmt {
    return { kind: "strategy-decl", name, namedArgs };
  },
  inputInt(
    name: string,
    defaultValue: number,
    title: string,
    opts: { minval?: number; maxval?: number; step?: number } = {},
  ): PineStmt {
    return { kind: "input-int", name, defaultValue, title, ...opts };
  },
  inputFloat(
    name: string,
    defaultValue: number,
    title: string,
    opts: { minval?: number; maxval?: number; step?: number } = {},
  ): PineStmt {
    return { kind: "input-float", name, defaultValue, title, ...opts };
  },
  inputBool(name: string, defaultValue: boolean, title: string): PineStmt {
    return { kind: "input-bool", name, defaultValue, title };
  },
  assign(
    target: string | string[],
    value: PineExpr,
    declaration?: PineVarDeclaration,
  ): PineStmt {
    return { kind: "assign", target, value, declaration };
  },
  reassign(target: string, value: PineExpr): PineStmt {
    return { kind: "reassign", target, value };
  },
  ifBlock(condition: PineExpr, body: PineStmt[]): PineStmt {
    return { kind: "if", condition, body };
  },
  callStmt(
    callee: string,
    args: PineExpr[] = [],
    namedArgs?: Array<[string, PineExpr]>,
  ): PineStmt {
    return { kind: "call-stmt", callee, args, namedArgs };
  },
};
